import { useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

/* Post-session feedback capture. Wired to /api/feedback + /api/feedback/dismiss.
   Props:
     - open: bool
     - sessionId?: string   — passed through to /feedback so admins can trace it
     - onSubmitted?: () -> void
     - onDismissed?: () -> void
*/

const USE_AGAIN_OPTIONS = [
  { value: "definitely", label: "Definitely" },
  { value: "maybe", label: "Maybe" },
  { value: "probably_not", label: "Probably Not" },
];

export function FeedbackModal({ open, sessionId, onSubmitted, onDismissed }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [useAgain, setUseAgain] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const canSubmit = rating >= 1 && rating <= 5 && !!useAgain && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        rating,
        would_use_again: useAgain,
        comment: comment.trim() || null,
        session_id: sessionId || null,
      });
      toast.success("Thanks for the feedback!");
      onSubmitted?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't send feedback.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const handleDismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await api.post("/feedback/dismiss");
    } catch {
      // still close the modal — server-side stale won't hurt
    }
    onDismissed?.();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !submitting) handleDismiss();
      }}
    >
      <DialogContent
        data-testid="feedback-modal"
        className="max-w-[560px] w-[calc(100vw-2rem)] border-white/10 bg-[#141414] p-0 text-white shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)] sm:rounded-2xl"
      >
        <div className="p-7 sm:p-8">
          <div className="mb-6">
            <DialogTitle asChild>
              <h2 className="font-heading text-2xl font-black tracking-tight text-white sm:text-3xl">
                How was that mock?
              </h2>
            </DialogTitle>
            <DialogDescription asChild>
              <p className="mt-1.5 text-sm text-zinc-400">
                20 seconds of your time helps us make SalesPit sharper for every rep.
              </p>
            </DialogDescription>
          </div>

          {/* Star rating */}
          <div className="space-y-2.5">
            <label className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">
              Rate your experience
            </label>
            <div
              className="flex items-center gap-1.5"
              data-testid="feedback-stars"
              onMouseLeave={() => setHoverRating(0)}
            >
              {[1, 2, 3, 4, 5].map((n) => {
                const active = (hoverRating || rating) >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    data-testid={`feedback-star-${n}`}
                    onMouseEnter={() => setHoverRating(n)}
                    onClick={() => setRating(n)}
                    className="rounded-md p-1 transition-transform hover:scale-110 active:scale-95"
                    aria-label={`Rate ${n} star${n === 1 ? "" : "s"}`}
                  >
                    <Star
                      size={30}
                      className={
                        active
                          ? "fill-[#E52525] text-[#E52525]"
                          : "fill-transparent text-white/25"
                      }
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Would use again */}
          <div className="mt-6 space-y-2.5">
            <label className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">
              Would you use SalesPit again?
            </label>
            <div className="flex flex-wrap gap-2">
              {USE_AGAIN_OPTIONS.map((opt) => {
                const active = useAgain === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`feedback-use-again-${opt.value}`}
                    onClick={() => setUseAgain(opt.value)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                      active
                        ? "border-[#E52525]/60 bg-[#E52525]/15 text-white"
                        : "border-white/10 bg-transparent text-zinc-300 hover:border-white/25 hover:bg-white/5"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Comment */}
          <div className="mt-6 space-y-2.5">
            <label
              htmlFor="feedback-comment"
              className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500"
            >
              One thing we could improve <span className="text-zinc-600">· optional</span>
            </label>
            <Textarea
              id="feedback-comment"
              data-testid="feedback-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="What would make your next call better?"
              className="resize-none border-white/10 bg-[#0F0F0F] text-sm text-white placeholder:text-zinc-600 focus-visible:ring-[#E52525]/40"
            />
          </div>

          {/* Actions */}
          <div className="mt-7 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
            <button
              type="button"
              data-testid="feedback-not-now-btn"
              disabled={submitting || dismissing}
              onClick={handleDismiss}
              className="rounded-full border border-white/10 bg-transparent px-5 py-2.5 text-sm font-semibold text-zinc-300 transition-colors hover:border-white/25 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Not Now
            </button>
            <button
              type="button"
              data-testid="feedback-submit-btn"
              disabled={!canSubmit}
              onClick={handleSubmit}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#E52525] px-6 py-2.5 text-sm font-semibold text-white shadow-[0_0_24px_rgba(229,37,37,0.35)] transition-[background-color,transform] hover:bg-[#FF3333] active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none disabled:hover:bg-[#E52525]"
            >
              {submitting && <Loader2 size={15} className="animate-spin" />}
              Submit Feedback
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FeedbackModal;
